import { listPrayerAttempts, getOwnerConflicts, getOwnerContentEntities, listTestimonyDrafts } from '@/lib/content-lifecycle/store';
import { hasNonEmptyRecoveryText } from '@/lib/content-escrow/semantic';
import { getAllAudioQueueRows } from '@/lib/db';
import { hasActiveVoiceCaptureWork } from '@/lib/voice-work-fence';
import { unsavedRecordingState } from '@/lib/voice-capture-custody';

/**
 * Read-only description of why Settings could not sign out (testimony plan
 * rev 4 section 9.4; Step 3 proposal section 3.5). `useAuth.signOut` and every
 * owner check stay the only authority: this module never decides whether a
 * sign-out may happen, it only names what it can observe, so the wording
 * says what is actually there. Anything it cannot name is reported as
 * `other`, with neutral wording and no promise about what happens next.
 */
export type SignOutCause =
  | 'recording-failed'
  | 'recording-unresolved'
  | 'recording-active'
  | 'home-words'
  | 'interrupted-words'
  | 'notes'
  | 'attempts'
  | 'recordings-kept'
  | 'unsent'
  | 'other';

export interface SignOutCauses {
  causes: SignOutCause[];
  /** A recording that lives only in this page's memory (failed or overdue). */
  inPageRecording: boolean;
  /** UnsavedRecordingWarning shows its Try again control (it does so only for a failed capture). */
  tryAgainShown: boolean;
  offline: boolean;
}

/** Which Settings recovery surface is on screen now, published by the surface itself. */
let notesListShownFor: { ownerId: string; authEpoch: number } | null = null;
let notesListVersion = 0;
const notesListListeners = new Set<() => void>();

export function setNotesListShown(owner: { ownerId: string; authEpoch: number } | null): void {
  const same = owner === null
    ? notesListShownFor === null
    : notesListShownFor !== null &&
      notesListShownFor.ownerId === owner.ownerId && notesListShownFor.authEpoch === owner.authEpoch;
  if (same) return;
  notesListShownFor = owner ? { ...owner } : null;
  notesListVersion += 1;
  for (const listener of notesListListeners) listener();
}

/** `useSyncExternalStore` pair, so a pointer to the list follows it. */
export function subscribeNotesListShown(listener: () => void): () => void {
  notesListListeners.add(listener);
  return () => { notesListListeners.delete(listener); };
}

export function notesListShownVersion(): number {
  return notesListVersion;
}

export function notesListShown(ownerId: string, authEpoch: number): boolean {
  return notesListShownFor !== null &&
    notesListShownFor.ownerId === ownerId && notesListShownFor.authEpoch === authEpoch;
}

function storedTextPresent(key: string): boolean {
  try {
    const value = localStorage.getItem(key);
    return value !== null && hasNonEmptyRecoveryText({ key, value });
  } catch {
    return false;
  }
}

async function safely<T>(read: () => Promise<T>): Promise<T | null> {
  try {
    return await read();
  } catch {
    return null;
  }
}

/**
 * Each check is independent and read-only; a check that fails contributes
 * nothing, and when nothing could be named the result is `other`.
 */
export async function readSignOutCauses(ownerId: string): Promise<SignOutCauses> {
  const causes: SignOutCause[] = [];
  let failed = 0;
  let unresolved = 0;
  try {
    ({ failed, unresolved } = unsavedRecordingState());
  } catch {
    failed = 0;
    unresolved = 0;
  }
  if (failed > 0) causes.push('recording-failed');
  if (unresolved > 0) causes.push('recording-unresolved');
  if (failed === 0 && unresolved === 0 && hasActiveVoiceCaptureWork()) causes.push('recording-active');
  if (storedTextPresent('adia-prayer-draft')) causes.push('home-words');
  if (storedTextPresent('adia-interrupted-interim')) causes.push('interrupted-words');

  const [drafts, attempts, rows, entities, conflicts] = await Promise.all([
    safely(() => listTestimonyDrafts(ownerId)),
    safely(() => listPrayerAttempts(ownerId)),
    safely(() => getAllAudioQueueRows()),
    safely(() => getOwnerContentEntities(ownerId)),
    safely(() => getOwnerConflicts(ownerId)),
  ]);
  if (drafts && drafts.length > 0) causes.push('notes');
  if (attempts && attempts.length > 0) causes.push('attempts');
  if (rows && rows.length > 0) causes.push('recordings-kept');
  const unsent = (entities ?? []).some((entity) => (
    entity.pending !== null || entity.state === 'dirty' || entity.state === 'conflicted'
  )) || (conflicts ?? []).length > 0;
  if (unsent) causes.push('unsent');
  if (causes.length === 0) causes.push('other');

  return {
    causes,
    inPageRecording: failed > 0 || unresolved > 0 || causes.includes('recording-active'),
    tryAgainShown: failed > 0,
    offline: typeof navigator !== 'undefined' && navigator.onLine === false,
  };
}
